import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import {
  Avatar,
  Button,
  IconPlus,
  IconWarehouse1,
  Input,
  Modal,
  ScreenLayout,
  SkeletonListRows,
  StatusBadge,
  usePendingAppAction,
  usePref,
  type ScreenAction,
  type ScreenStat,
} from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  CAT_TYPE,
  DEFAULT_TASK_STATUS,
  HOUSE_TYPE,
  TASK_STATUSES,
  TASK_TYPE,
  assetToCat,
  catHousePatch,
  formatDateIso,
  isActive,
  isTaskClosed,
  isType,
  outsideCatIdsOf,
  placeToHouse,
  taskStatusOptionOf,
  ticketToTask,
  todayIso,
  type Cat,
  type House,
  type Task,
  type TaskStatus,
} from './data.js';
import {
  DEFAULT_FILTERS,
  DEFAULT_HOUSE_STATS,
  DEFAULT_TASK_STATS,
  FILTERS_PREF_KEY,
  HOUSES_STATS_PREF_KEY,
  HOUSE_STAT_DEFS,
  STATS_MAX,
  TASKS_STATS_PREF_KEY,
  TASK_STAT_DEFS,
  type HouseStatId,
  type TaskStatId,
} from './settings-defs.js';

/**
 * Ecran unique de l'app Cat Houses.
 *
 * Un SEUL `ScreenLayout` (regle du gabarit : le menu SEGMENTE les donnees, il ne
 * multiplie pas les layouts). Deux sections pilotees par un state local :
 *  - `houses` : les maisons ou vivent les chats (onglet 1) ;
 *  - `tasks`  : les tickets techniques poses sur une maison (onglet 2).
 * Les reglages de l'app (visibilite des pastilles de statut, stats d'en-tete)
 * vivent dans settings.tsx, monte par le hub Parametres du shell (route
 * /settings) : l'ecran LIT les memes prefs ("app.cat-houses.filters",
 * "app.cat-houses.stats.*", cf. settings-defs.ts) via `usePref` et se
 * resynchronise en direct. Les formulaires s'ouvrent dans le composant `Modal`
 * de @taki/ui, jamais dans la zone centrale (regle G6).
 *
 * Donnees (cage IA) : 100% via `@taki/sdk` obtenu par `useTakiApp()`. Le detail
 * du choix des quatre familles du socle vit dans `data.ts` (le contrat) :
 *  - une maison = `place`   de type 'cat-house'      (nom dans customFields) ;
 *  - un chat    = `asset`   de type 'cat'            (partage avec les apps soeurs) ;
 *  - une sortie = `custody` de type 'outside-visit'  (LECTURE SEULE ici) ;
 *  - un ticket  = `ticket`  de type 'maintenance'    (la demande ; le travail
 *    sera fait par la future app Maintenance, qui creera l'ordre de travail
 *    pointant dessus via `workOrder.ticketId`).
 */

/** Section active de l'ecran (le menu de la zone 1). */
type Section = 'houses' | 'tasks';

/** Pastille "tout voir" de la zone 4 : elle ne se masque jamais. */
const FILTER_ALL = 'tous';

/** Libelle d'une maison, meme si son nom d'usage manque. */
function houseLabelOf(house: House | null): string {
  if (house == null || house.name === '') {
    return 'Maison sans nom';
  }
  return house.name;
}

export function CatHousesScreen(): JSX.Element {
  const { sdk, config } = useTakiApp();

  // --- Etat de l'ecran -----------------------------------------------------
  const [section, setSection] = useState<Section>('houses');
  const [query, setQuery] = useState('');
  const [activeFilterId, setActiveFilterId] = useState<string>(FILTER_ALL);

  const [houses, setHouses] = useState<House[]>([]);
  const [cats, setCats] = useState<Cat[]>([]);
  const [outsideCatIds, setOutsideCatIds] = useState<Set<string>>(new Set());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Formulaires (modals) ------------------------------------------------
  // Un SEUL modal ouvert a la fois par ecran (doctrine du composant Modal).
  const [openModal, setOpenModal] = useState<'house' | 'residents' | 'task' | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [formHouseName, setFormHouseName] = useState('');
  const [formHouseAddress, setFormHouseAddress] = useState('');

  const [formTaskDate, setFormTaskDate] = useState(todayIso);
  const [formTaskHouseId, setFormTaskHouseId] = useState('');
  const [formTaskNote, setFormTaskNote] = useState('');

  /** La maison dont on gere les residents (modal 'residents'). */
  const [residentsHouseId, setResidentsHouseId] = useState('');

  /**
   * Prefs partagees avec settings.tsx (hub Parametres du shell) : visibilite
   * des pastilles de statut (zone 4) et stats d'en-tete affichees (zone 3),
   * une selection par onglet. Locales au poste, resynchronisees en direct via
   * l'event "taki:pref-change".
   */
  const [filterVisibility] = usePref<Record<string, boolean>>(FILTERS_PREF_KEY, DEFAULT_FILTERS);
  const [houseStatIds] = usePref<string[]>(HOUSES_STATS_PREF_KEY, DEFAULT_HOUSE_STATS);
  const [taskStatIds] = usePref<string[]>(TASKS_STATS_PREF_KEY, DEFAULT_TASK_STATS);

  // --- Chargement ----------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [places, assets, custodies, tickets] = await Promise.all([
        sdk.places.list(),
        sdk.assets.list(),
        sdk.custodies.list(),
        sdk.tickets.list(),
      ]);
      setHouses(
        places
          .filter((p) => isType(p, HOUSE_TYPE) && isActive(p))
          .map(placeToHouse)
          .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
      );
      setCats(
        assets
          .filter((a) => isType(a, CAT_TYPE) && isActive(a))
          .map(assetToCat)
          .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
      );
      // Les sorties sont ecrites par cat-logger-outside : ici, lecture seule.
      setOutsideCatIds(outsideCatIdsOf(custodies));
      setTasks(
        tickets
          .filter((t) => isType(t, TASK_TYPE) && isActive(t))
          .map(ticketToTask)
          .sort((a, b) => {
            // Date pure 'YYYY-MM-DD' : l'ordre alphabetique EST l'ordre chronologique.
            if (a.dueDate === b.dueDate) {
              return a.note.localeCompare(b.note, 'fr');
            }
            if (a.dueDate === '') {
              return 1;
            }
            if (b.dueDate === '') {
              return -1;
            }
            return a.dueDate.localeCompare(b.dueDate);
          }),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erreur inconnue.');
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  useEffect(() => {
    void load();
  }, [load]);

  // --- Donnees derivees ----------------------------------------------------

  const houseById = useMemo(() => new Map(houses.map((house) => [house.id, house])), [houses]);

  /** Les chats de chaque maison (cle = id du place). */
  const catsByHouseId = useMemo(() => {
    const map = new Map<string, Cat[]>();
    for (const cat of cats) {
      if (cat.houseId === '') {
        continue;
      }
      const current = map.get(cat.houseId);
      if (current == null) {
        map.set(cat.houseId, [cat]);
      } else {
        current.push(cat);
      }
    }
    return map;
  }, [cats]);

  /** Les chats sans maison : le vivier du modal des residents. */
  const homelessCats = useMemo(() => cats.filter((cat) => cat.houseId === ''), [cats]);

  /**
   * Les chats reellement loges : ceux dont la maison existe encore. Un houseId
   * qui ne pointe plus rien ne doit pas gonfler la stat.
   */
  const housedCats = useMemo(
    () => cats.filter((cat) => houseById.has(cat.houseId)),
    [cats, houseById],
  );

  /** Chats loges actuellement dehors : la somme des "en sortie" des lignes maison. */
  const housedOutsideCount = useMemo(
    () => housedCats.filter((cat) => outsideCatIds.has(cat.id)).length,
    [housedCats, outsideCatIds],
  );

  const taskCountByStatus = useMemo(() => {
    const counts: Record<TaskStatus, number> = { ouvert: 0, en_cours: 0, clos: 0 };
    for (const task of tasks) {
      counts[task.status] += 1;
    }
    return counts;
  }, [tasks]);

  /** Le nombre de chats dehors dans une maison donnee. */
  const outsideCountOf = useCallback(
    (houseId: string) =>
      (catsByHouseId.get(houseId) ?? []).filter((cat) => outsideCatIds.has(cat.id)).length,
    [catsByHouseId, outsideCatIds],
  );

  /** La maison ouverte dans le modal des residents (relue apres chaque rechargement). */
  const residentsHouse = useMemo(
    () => houseById.get(residentsHouseId) ?? null,
    [houseById, residentsHouseId],
  );
  const residentsCats = useMemo(
    () => catsByHouseId.get(residentsHouseId) ?? [],
    [catsByHouseId, residentsHouseId],
  );
  const residentsOutsideCount = useMemo(
    () => residentsCats.filter((cat) => outsideCatIds.has(cat.id)).length,
    [residentsCats, outsideCatIds],
  );

  // --- Recherche et filtres (cote client, v0) ------------------------------

  const needle = query.trim().toLowerCase();

  /** Maisons filtrees par la recherche : nom + adresse. */
  const visibleHouses = useMemo(() => {
    if (needle === '') {
      return houses;
    }
    return houses.filter(
      (house) =>
        house.name.toLowerCase().includes(needle) || house.address.toLowerCase().includes(needle),
    );
  }, [houses, needle]);

  /** Tickets filtres par la pastille de statut, puis par la recherche : note + nom de maison. */
  const visibleTasks = useMemo(() => {
    const byStatus =
      activeFilterId === FILTER_ALL ? tasks : tasks.filter((task) => task.status === activeFilterId);
    if (needle === '') {
      return byStatus;
    }
    return byStatus.filter((task) => {
      const houseName = houseLabelOf(houseById.get(task.houseId) ?? null).toLowerCase();
      return task.note.toLowerCase().includes(needle) || houseName.includes(needle);
    });
  }, [tasks, activeFilterId, needle, houseById]);

  /**
   * Pastilles de la zone 4 : "Tous" + les statuts laisses visibles par les
   * reglages (pref, cle absente = visible).
   */
  const filters = useMemo(
    () => [
      { id: FILTER_ALL, label: 'Tous' },
      ...TASK_STATUSES.filter((option) => filterVisibility[option.value] !== false).map(
        (option) => ({ id: option.value, label: option.label }),
      ),
    ],
    [filterVisibility],
  );

  // Si la pastille ACTIVE vient d'etre masquee dans les reglages, le filtre
  // retombe sur "Tous" (jamais de filtre actif invisible).
  useEffect(() => {
    if (activeFilterId !== FILTER_ALL && filterVisibility[activeFilterId] === false) {
      setActiveFilterId(FILTER_ALL);
    }
  }, [filterVisibility, activeFilterId]);

  // --- Actions -------------------------------------------------------------

  const closeModal = useCallback(() => {
    setOpenModal(null);
    setSaveError(null);
  }, []);

  const openHouseForm = useCallback(() => {
    setFormHouseName('');
    setFormHouseAddress('');
    setSaveError(null);
    setOpenModal('house');
  }, []);

  const openTaskForm = useCallback(() => {
    setFormTaskDate(todayIso());
    setFormTaskHouseId('');
    setFormTaskNote('');
    setSaveError(null);
    setOpenModal('task');
  }, []);

  const openResidents = useCallback((house: House) => {
    setResidentsHouseId(house.id);
    setSaveError(null);
    setOpenModal('residents');
  }, []);

  // --- Actions du dock (shortcuts) ------------------------------------------
  // Le shell pose l'action (taki.app.json, champ `actions`) puis navigue ici ;
  // l'app se place sur l'onglet de l'action, ouvre le Modal correspondant et
  // consomme. `actionId` repasse a null apres consume() : l'effet ne rejoue
  // pas, pas de double ouverture.
  const { actionId, consume } = usePendingAppAction('cat-houses');
  useEffect(() => {
    if (actionId == null) {
      return;
    }
    if (actionId === 'new-house') {
      setSection('houses');
      openHouseForm();
    } else if (actionId === 'new-task') {
      setSection('tasks');
      openTaskForm();
    }
    consume();
  }, [actionId, consume, openHouseForm, openTaskForm]);

  /** Cree la maison : un place 'cat-house' interne, nom d'usage dans customFields. */
  const handleSaveHouse = useCallback(async () => {
    const name = formHouseName.trim();
    const address = formHouseAddress.trim();
    if (name === '') {
      setSaveError('Renseigne le nom de la maison.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await sdk.places.create({
        orgId: config.orgId,
        type: HOUSE_TYPE,
        isInternal: true,
        // `address` refuse la chaine vide : on ne l'ecrit que si elle est remplie.
        ...(address !== '' ? { address } : {}),
        customFields: { name },
      });
      setOpenModal(null);
      setFormHouseName('');
      setFormHouseAddress('');
      await load();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }, [formHouseName, formHouseAddress, sdk, config.orgId, load]);

  /** Ouvre le ticket : une demande 'maintenance' qui designe la maison visee. */
  const handleSaveTask = useCallback(async () => {
    const note = formTaskNote.trim();
    if (formTaskHouseId === '') {
      setSaveError('Choisis la maison concernee.');
      return;
    }
    if (formTaskDate === '') {
      setSaveError('Renseigne la date prevue.');
      return;
    }
    if (note === '') {
      setSaveError('Renseigne la note technique.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await sdk.tickets.create({
        orgId: config.orgId,
        type: TASK_TYPE,
        status: DEFAULT_TASK_STATUS,
        // La famille ticket n'a pas de lien natif vers un lieu : la maison visee
        // vit dans le sac. dueDate est une date PURE : 'YYYY-MM-DD', pas un instant.
        customFields: { houseId: formTaskHouseId, dueDate: formTaskDate, note },
      });
      setOpenModal(null);
      setFormTaskHouseId('');
      setFormTaskNote('');
      setFormTaskDate(todayIso());
      await load();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  }, [formTaskNote, formTaskHouseId, formTaskDate, sdk, config.orgId, load]);

  /**
   * Ferme le ticket. C'est la SEULE ecriture de statut de cette app : elle emet
   * la demande et peut la retirer, elle ne la prend pas en charge. Le passage en
   * 'en_cours' appartient a la future app Maintenance.
   */
  const handleCloseTask = useCallback(
    async (task: Task) => {
      if (isTaskClosed(task.status)) {
        return;
      }
      setSaving(true);
      setSaveError(null);
      try {
        // Patch partiel : le sac customFields (maison, dueDate, note) n'est pas
        // touche. `updatedAt` date la cloture, la famille ticket n'a pas de
        // champ de date de fin.
        await sdk.tickets.update(task.id, { status: 'clos' });
        await load();
      } catch (cause) {
        setSaveError(cause instanceof Error ? cause.message : 'Mise a jour impossible.');
      } finally {
        setSaving(false);
      }
    },
    [sdk, load],
  );

  /**
   * Change la maison d'un chat ('' le retire de la sienne).
   * On ne poste que la cle `houseId` : le SDK fusionne le sac cle par cle, les
   * champs des apps soeurs restent intacts (cf. `catHousePatch`).
   */
  const handleSetCatHouse = useCallback(
    async (cat: Cat, houseId: string) => {
      setSaving(true);
      setSaveError(null);
      try {
        await sdk.assets.update(cat.id, { customFields: catHousePatch(houseId) });
        await load();
      } catch (cause) {
        setSaveError(cause instanceof Error ? cause.message : 'Mise a jour impossible.');
      } finally {
        setSaving(false);
      }
    },
    [sdk, load],
  );

  // --- Rendu ---------------------------------------------------------------

  // Valeurs de TOUTES les stats des catalogues, calculees des donnees reelles.
  // Les prefs ne choisissent que LESQUELLES afficher, jamais les valeurs.
  const houseStatValues: Record<HouseStatId, string> = {
    houses: String(houses.length),
    housed: String(housedCats.length),
    'housed-outside': String(housedOutsideCount),
  };
  const taskStatValues: Record<TaskStatId, string> = {
    ouvert: String(taskCountByStatus.ouvert),
    en_cours: String(taskCountByStatus.en_cours),
    clos: String(taskCountByStatus.clos),
  };

  // Stats de la zone 3 : la selection des reglages (une par onglet), dans
  // l'ordre du catalogue, plafonnee a STATS_MAX (garde-fou si la pref stockee
  // depasse le contrat).
  const headerStatsBySection: Record<Section, ScreenStat[]> = {
    houses: HOUSE_STAT_DEFS.filter((def) => houseStatIds.includes(def.id))
      .slice(0, STATS_MAX)
      .map((def) => ({ value: houseStatValues[def.id], label: def.label })),
    tasks: TASK_STAT_DEFS.filter((def) => taskStatIds.includes(def.id))
      .slice(0, STATS_MAX)
      .map((def) => ({ value: taskStatValues[def.id], label: def.label })),
  };

  // L'etat vide ne depend JAMAIS d'un formulaire : le modal flotte au-dessus.
  const emptyBySection: Record<Section, boolean> = {
    houses: houses.length === 0,
    tasks: tasks.length === 0,
  };

  const emptyTitleBySection: Record<Section, string> = {
    houses: 'Aucune maison enregistree',
    tasks: 'Aucun ticket ouvert',
  };

  const emptyHintBySection: Record<Section, string> = {
    houses: 'Clique sur "Ajouter", puis ajoute-lui des chats.',
    tasks:
      houses.length === 0
        ? 'Un ticket vise une maison : cree d\'abord une maison dans l\'onglet "Maisons".'
        : 'Clique sur "Ajouter".',
  };

  // Contrat v2 : l'action d'ajout se declare `kind: 'add'`, le gabarit impose
  // le libelle universel "Ajouter", l'icone IconPlus et le lime (regle G5).
  const quickActionsBySection: Record<Section, ScreenAction[]> = {
    houses: [
      {
        id: 'new-house',
        label: 'Ajouter',
        kind: 'add',
        disabled: loading || error != null,
        onClick: openHouseForm,
      },
    ],
    tasks: [
      {
        id: 'new-task',
        label: 'Ajouter',
        kind: 'add',
        // Un ticket vise une maison : sans maison, le formulaire n'a rien a proposer.
        disabled: loading || error != null || houses.length === 0,
        onClick: openTaskForm,
      },
    ],
  };

  return (
    <>
      <ScreenLayout
        appName="Cat Houses"
        appIcon={<IconWarehouse1 size={18} />}
        menuItems={[
          { id: 'houses', label: 'Maisons', count: houses.length },
          { id: 'tasks', label: 'Tickets', count: tasks.length },
        ]}
        activeMenuId={section}
        onMenuSelect={(id) => setSection(id === 'tasks' ? 'tasks' : 'houses')}
        /* Zone 2 : le h1 affiche le label de l'onglet actif ; `title` reste le repli. */
        title="Maisons des chats"
        headerStats={headerStatsBySection[section]}
        onSearchChange={setQuery}
        searchValue={query}
        searchPlaceholder={
          section === 'houses' ? 'Rechercher une maison' : 'Rechercher un ticket'
        }
        // Les pastilles RAFFINENT la section courante : seuls les tickets ont des statuts.
        filters={section === 'tasks' ? filters : undefined}
        activeFilterId={activeFilterId}
        onFilterSelect={setActiveFilterId}
        // Les boutons appartiennent a l'entree de menu ACTIVE (regle G7).
        quickActions={quickActionsBySection[section]}
        loading={loading && houses.length === 0 && tasks.length === 0}
        /* Skeleton fidele a la ligne active : la ligne Maison porte un Avatar 30,
           la ligne Ticket n'en a pas. `withAvatar` suit la section pour zero saut
           de mise en page quand le contenu reel remplace le skeleton. */
        skeleton={<SkeletonListRows withAvatar={section === 'houses'} />}
        error={error}
        errorTitle="Echec du chargement"
        onRetry={() => void load()}
        retryLabel="Reessayer"
        empty={emptyBySection[section]}
        emptyTitle={emptyTitleBySection[section]}
        emptyHint={emptyHintBySection[section]}
      >
        {/* Onglet 1 : les maisons. */}
        {section === 'houses' && houses.length > 0 && (
          <ul>
            {visibleHouses.length === 0 && (
              <li className="px-5 py-4 text-sm text-ink-500">
                Aucune maison ne correspond a "{query}".
              </li>
            )}
            {visibleHouses.map((house) => {
              const label = houseLabelOf(house);
              const catCount = (catsByHouseId.get(house.id) ?? []).length;
              const outside = outsideCountOf(house.id);
              return (
                <li
                  key={house.id}
                  className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5 hover:bg-surface-1"
                >
                  <Avatar size={30} initials={label.slice(0, 2)} />
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink-900">{label}</span>
                    <span className="block truncate text-xs text-ink-500">
                      {house.address !== '' ? house.address : 'Adresse non renseignee'}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2.5">
                    <StatusBadge tone="neutral" label={`${catCount} chat${catCount > 1 ? 's' : ''}`} />
                    {outside > 0 && <StatusBadge tone="accent" label={`${outside} en sortie`} />}
                    {/* Pas de onClick sur la ligne : la vue de detail reste hors perimetre v0.
                        L'acces passe par ce bouton, et le modal est un formulaire d'edition. */}
                    <Button size="sm" variant="secondary" onClick={() => openResidents(house)}>
                      Gerer les chats
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Onglet 2 : les tickets. */}
        {section === 'tasks' && tasks.length > 0 && (
          <ul>
            {visibleTasks.length === 0 && (
              <li className="px-5 py-4 text-sm text-ink-500">
                Aucun ticket ne correspond a la recherche ou au filtre en cours.
              </li>
            )}
            {visibleTasks.map((task) => {
              const option = taskStatusOptionOf(task.status);
              const houseName = houseLabelOf(houseById.get(task.houseId) ?? null);
              return (
                <li
                  key={task.id}
                  className="flex items-center gap-3.5 border-b border-border-soft px-5 py-2.5 hover:bg-surface-1"
                >
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink-900">
                      {task.note !== '' ? task.note : 'Note non renseignee'}
                    </span>
                    <span className="block truncate text-xs text-ink-500">
                      {houseName} - prevu le {formatDateIso(task.dueDate)}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2.5">
                    {/* La led du ticket : vive tant qu'il y a a faire, eteinte une fois ferme. */}
                    <StatusBadge tone={option.tone} label={option.label} />
                    {/* Seule action de cette app sur un ticket : le fermer. */}
                    {!isTaskClosed(task.status) && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={saving}
                        onClick={() => void handleCloseTask(task)}
                      >
                        Fermer
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ScreenLayout>

      {/* Modal : nouvelle maison (regle G6 : jamais en zone centrale). */}
      <Modal
        open={openModal === 'house'}
        onClose={closeModal}
        title="Nouvelle maison"
        size="sm"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={saving} onClick={closeModal}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => void handleSaveHouse()}
            >
              Creer la maison
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="house-name" className="text-xs font-medium text-ink-500">
              Nom de la maison
            </label>
            <Input
              id="house-name"
              type="text"
              className="h-10"
              placeholder="Ex. Maison du jardin"
              value={formHouseName}
              onChange={(event) => setFormHouseName(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="house-address" className="text-xs font-medium text-ink-500">
              Adresse
            </label>
            <Input
              id="house-address"
              type="text"
              className="h-10"
              placeholder="Optionnel"
              value={formHouseAddress}
              onChange={(event) => setFormHouseAddress(event.target.value)}
            />
          </div>

          <p className="text-xs text-ink-500">
            Les chats s'ajoutent ensuite depuis le bouton "Gerer les chats" de la maison.
          </p>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>

      {/* Modal : nouveau ticket (onglet 2). */}
      <Modal
        open={openModal === 'task'}
        onClose={closeModal}
        title="Nouveau ticket"
        size="lg"
        footer={
          <>
            <Button size="sm" variant="secondary" disabled={saving} onClick={closeModal}>
              Annuler
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={saving}
              onClick={() => void handleSaveTask()}
            >
              Ouvrir le ticket
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="task-date" className="text-xs font-medium text-ink-500">
              Date prevue
            </label>
            <Input
              id="task-date"
              type="date"
              className="h-10"
              value={formTaskDate}
              onChange={(event) => setFormTaskDate(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Maison concernee</span>
            {houses.length === 0 ? (
              <p className="text-xs text-ink-500">Aucune maison enregistree.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {houses.map((house) => (
                  <Button
                    key={house.id}
                    size="sm"
                    variant={house.id === formTaskHouseId ? 'primary' : 'secondary'}
                    onClick={() => setFormTaskHouseId(house.id)}
                  >
                    {houseLabelOf(house)}
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="task-note" className="text-xs font-medium text-ink-500">
              Note technique
            </label>
            <Input
              id="task-note"
              type="text"
              className="h-10"
              placeholder="Ex. Reparer la chatiere de la porte arriere"
              value={formTaskNote}
              onChange={(event) => setFormTaskNote(event.target.value)}
            />
          </div>

          <p className="text-xs text-ink-500">
            Le ticket s'ouvre au statut "Ouvert" et sa led brille tant qu'il n'est pas ferme.
            C'est Maintenance qui le prendra en charge ; d'ici, tu peux seulement le fermer.
          </p>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>

      {/* Modal : les residents d'une maison (le coeur de l'onglet 1).
          Les changements s'appliquent immediatement : pas de brouillon, donc pas
          de bouton "Enregistrer", juste "Fermer". */}
      <Modal
        open={openModal === 'residents' && residentsHouse != null}
        onClose={closeModal}
        title={`Maison ${houseLabelOf(residentsHouse)}`}
        size="lg"
        footer={
          <Button size="sm" variant="secondary" onClick={closeModal}>
            Fermer
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-900">
            {residentsCats.length <= 1
              ? `${residentsCats.length} chat vit ici`
              : `${residentsCats.length} chats vivent ici`}
            , {residentsOutsideCount} en sortie.
          </p>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Chats de la maison</span>
            {residentsCats.length === 0 ? (
              <p className="text-xs text-ink-500">
                Aucun chat ne vit ici pour l'instant : ajoute-en un ci-dessous.
              </p>
            ) : (
              <ul className="flex flex-col">
                {residentsCats.map((cat) => (
                  <li
                    key={cat.id}
                    className="flex items-center gap-3 border-b border-border-soft py-2.5"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-ink-900">{cat.name}</span>
                    {outsideCatIds.has(cat.id) && <StatusBadge tone="accent" label="En sortie" />}
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={saving}
                      onClick={() => void handleSetCatHouse(cat, '')}
                    >
                      Retirer
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-500">Ajouter un chat</span>
            {homelessCats.length === 0 ? (
              <p className="text-xs text-ink-500">
                Aucun chat sans maison. Les fiches chats se creent dans Cat Manager Six ou Cat
                Logger Outside ; retirer un chat de sa maison le rend disponible ici.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {homelessCats.map((cat) => (
                  <Button
                    key={cat.id}
                    size="sm"
                    variant="secondary"
                    disabled={saving || residentsHouse == null}
                    onClick={() => {
                      if (residentsHouse != null) {
                        void handleSetCatHouse(cat, residentsHouse.id);
                      }
                    }}
                  >
                    <IconPlus size={15} />
                    {cat.name}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {saveError != null && <p className="text-xs text-danger">{saveError}</p>}
        </div>
      </Modal>
    </>
  );
}

export default CatHousesScreen;
